import React, { useState } from 'react';
import { ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const FormsRules = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Civil', 'Criminal', 'Bankruptcy', 'Appellate', 'Miscellaneous'];

  const forms = [
    { number: 'AO 10', title: 'Financial Disclosure Report', category: 'Miscellaneous', updated: 'Jan 2024', desc: 'Required annual disclosure for judicial officers and certain employees.' },
    { number: 'AO 85', title: 'Notice, Consent, and Reference of a Civil Action', category: 'Civil', updated: 'Dec 2023', desc: 'Used to consent to a magistrate judge presiding over a civil case.' },
    { number: 'AO 93', title: 'Search and Seizure Warrant', category: 'Criminal', updated: 'Mar 2024', desc: 'Standard form for applications for search and seizure warrants.' },
    { number: 'AO 106', title: 'Application for a Search Warrant', category: 'Criminal', updated: 'Mar 2024', desc: 'Application to a federal magistrate or district judge.' },
    { number: 'AO 240', title: 'Application to Proceed in District Court Without Prepaying Fees', category: 'Civil', updated: 'Feb 2024', desc: 'In forma pauperis application for civil cases.' },
    { number: 'B 101', title: 'Voluntary Petition for Individuals', category: 'Bankruptcy', updated: 'Dec 2023', desc: 'Standard petition form for individual debtors filing for bankruptcy protection.' },
    { number: 'B 200', title: 'Required Lists, Schedules, Statements', category: 'Bankruptcy', updated: 'Dec 2023', desc: 'Required schedules for bankruptcy filings including assets and liabilities.' },
    { number: 'AO 450', title: 'Judgment in a Civil Case', category: 'Civil', updated: 'Jan 2024', desc: 'Form for the entry of judgment in civil actions.' },
  ];

  const rulesCategories = [
    {
      title: 'Federal Rules of Civil Procedure',
      abbr: 'FRCP',
      desc: 'Govern the conduct of all civil actions in U.S. district courts.',
      effective: 'Dec 1, 2024',
      color: 'bg-blue-600',
    },
    {
      title: 'Federal Rules of Criminal Procedure',
      abbr: 'FRCrP',
      desc: 'Govern the procedure in all criminal proceedings in U.S. district courts.',
      effective: 'Dec 1, 2023',
      color: 'bg-[#20476f]',
    },
    {
      title: 'Federal Rules of Evidence',
      abbr: 'FRE',
      desc: 'Govern admissibility of evidence in proceedings in federal courts.',
      effective: 'Dec 1, 2023',
      color: 'bg-purple-700',
    },
    {
      title: 'Federal Rules of Appellate Procedure',
      abbr: 'FRAP',
      desc: 'Govern procedure in all U.S. Courts of Appeals.',
      effective: 'Dec 1, 2024',
      color: 'bg-teal-700',
    },
    {
      title: 'Federal Rules of Bankruptcy Procedure',
      abbr: 'FRBP',
      desc: 'Govern practice and procedure in cases under the Bankruptcy Code.',
      effective: 'Dec 1, 2024',
      color: 'bg-red-700',
    },
  ];

  const filteredForms = forms.filter((f) => {
    const matchCategory = activeCategory === 'All' || f.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      f.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.number.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div
        className="relative py-20 px-8"
        style={{
          background: 'linear-gradient(135deg, #0d3559 0%, #20476f 40%, #2471a3 100%)',
        }}
      >
        <div className="max-w-[65rem] mx-auto relative z-10">
          <p className="text-blue-200 text-sm mb-4">Home &rsaquo; Forms &amp; Rules</p>
          <h1
            className="text-5xl font-light text-white mb-6 leading-tight"
            style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
          >
            Forms &amp; Rules
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl leading-relaxed mb-8">
            Search for national federal court forms by keyword, form number, or category.
            Access the Federal Rules of Practice and Procedure.
          </p>

          {/* Search */}
          <div className="relative max-w-xl">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by form name or number..."
              className="w-full pl-5 pr-14 py-4 rounded-full bg-white text-gray-800 placeholder-gray-400 focus:outline-none shadow-lg"
            />
            <button className="absolute right-4 top-1/2 -translate-y-1/2 text-[#0057a3]">
              <MagnifyingGlassIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Rules of Procedure Section */}
      <div className="bg-gray-50 border-b border-gray-200 py-12">
        <div className="max-w-[65rem] mx-auto px-8">
          <h2
            className="text-2xl font-semibold text-gray-900 mb-8"
            style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
          >
            Federal Rules of Practice &amp; Procedure
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {rulesCategories.map((rule) => (
              <div
                key={rule.abbr}
                className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md hover:border-[#0057a3] transition-all cursor-pointer group"
              >
                <div className={`w-12 h-12 ${rule.color} rounded-xl flex items-center justify-center mb-4`}>
                  <span className="text-white text-xs font-bold leading-tight text-center">{rule.abbr}</span>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-2 leading-tight group-hover:text-[#0057a3] transition-colors">
                  {rule.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">{rule.desc}</p>
                <div className="text-xs text-green-600 font-medium">
                  Effective {rule.effective}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <a href="#" className="inline-flex items-center text-[#0057a3] font-medium hover:underline text-sm">
              View all pending and recent rule amendments
              <ArrowRightIcon className="w-4 h-4 ml-1.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Forms Section */}
      <div className="max-w-[65rem] mx-auto px-8 py-16">
        <div className="flex flex-col md:flex-row gap-12">

          {/* Forms Table */}
          <div className="flex-1">
            <h2
              className="text-2xl font-semibold text-gray-900 mb-6"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              Find a Form
            </h2>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? 'bg-[#0057a3] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Forms list */}
            <div className="space-y-3">
              {filteredForms.length > 0 ? (
                filteredForms.map((form) => (
                  <div
                    key={form.number}
                    className="flex items-start gap-4 p-5 border border-gray-200 rounded-xl hover:border-[#0057a3] hover:bg-blue-50/30 transition-all cursor-pointer group"
                  >
                    <div className="w-14 h-14 bg-[#20476f] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0057a3] transition-colors">
                      <div className="text-center">
                        <div className="text-white text-xs font-bold leading-tight">{form.number}</div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900 group-hover:text-[#0057a3] transition-colors">
                          {form.title}
                        </h3>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                          {form.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 leading-relaxed">{form.desc}</p>
                      <span className="text-xs text-gray-400">Updated {form.updated}</span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button className="text-xs px-3 py-1.5 bg-[#0057a3] text-white rounded-full hover:bg-blue-700 transition-colors">
                        PDF
                      </button>
                      <button className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-full hover:border-[#0057a3] hover:text-[#0057a3] transition-colors">
                        Word
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16 text-gray-400">
                  <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No forms found</p>
                  <p className="text-sm">Try a different search term or category</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0 space-y-6">
            {/* Help box */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Can't find a specific form? Contact your local court's clerk's office for assistance.
              </p>
              <a href="#" className="inline-flex items-center text-[#0057a3] text-sm font-medium hover:underline">
                Find a court <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </div>

            {/* Quick links */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-3">
                {[
                  'Bankruptcy Forms',
                  'Civil Cover Sheet',
                  'Pro Se Forms',
                  'Criminal Justice Act Forms',
                  'Appellate Forms',
                ].map((link) => (
                  <a
                    key={link}
                    href="#"
                    className="flex items-center justify-between text-sm text-[#0057a3] hover:underline"
                  >
                    {link}
                    <ArrowRightIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>

            {/* Local rules note */}
            <div className="bg-[#20476f] rounded-2xl p-6 text-white">
              <h3 className="font-semibold mb-2">Local Court Rules</h3>
              <p className="text-sm text-blue-200 leading-relaxed mb-4">
                Individual courts may have local rules supplementing the national rules. Always check local rules.
              </p>
              <a href="#" className="inline-flex items-center text-[#efd388] text-sm font-medium hover:underline">
                Browse local rules <ArrowRightIcon className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom amendments banner */}
      <div className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-[65rem] mx-auto px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white border border-gray-200 rounded-2xl p-8">
            <div>
              <h3
                className="text-xl font-semibold text-gray-900 mb-2"
                style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
              >
                December 2024 Rules Amendments Now in Effect
              </h3>
              <p className="text-gray-600 text-sm">
                The latest amendments to the Federal Rules became effective December 1, 2024.
                Review the changes that may affect your filings.
              </p>
            </div>
            <button className="inline-flex items-center px-6 py-3 bg-[#0057a3] hover:bg-blue-700 text-white font-medium rounded-full transition-colors flex-shrink-0">
              Read the Changes
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormsRules;